import { useMemo } from "react";

/**
 * GoldGlitter
 * --------------------------------------------------------------------------
 * A pure-CSS particle rain that paints ~70 sparkly gold flecks falling from
 * the top of the viewport with random delays, durations, sizes, lateral
 * drift, and twinkle phases. Used as a fixed full-screen overlay whenever
 * the "Golden Dumpling" theme is active, plus inside the Dumpling page
 * hero so the moment a crew member opens Goldie's world they're showered
 * with glitter.
 *
 * Implementation notes:
 *   - Particles are deterministic per mount (useMemo) so the layout is
 *     stable on re-renders but feels random across page loads.
 *   - Three sparkle "shapes" (round, plus, 4-point star) are mixed in via
 *     SVG inline data URIs to avoid an HTTP round-trip for tiny art.
 *   - Pointer-events: none so glitter never blocks taps.
 *   - Honors prefers-reduced-motion via the parent CSS rule (see index.css
 *     `.gold-glitter-reduce` override; we just stop the animation there).
 */

const COLORS = ["#FACC15", "#FCD34D", "#FDE68A", "#F59E0B", "#FFFFFF"];

const SHAPES = [
  // round dot
  (color) => `data:image/svg+xml;utf8,${encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 10 10'><circle cx='5' cy='5' r='4' fill='${color}'/></svg>`
  )}`,
  // 4-point sparkle
  (color) => `data:image/svg+xml;utf8,${encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 32'><path d='M16 0 L18 14 L32 16 L18 18 L16 32 L14 18 L0 16 L14 14 Z' fill='${color}'/></svg>`
  )}`,
  // plus
  (color) => `data:image/svg+xml;utf8,${encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12'><path d='M6 0v12M0 6h12' stroke='${color}' stroke-width='2.5' stroke-linecap='round'/></svg>`
  )}`,
];

export default function GoldGlitter({ count, scope = "fixed", testId = "gold-glitter" }) {
  // Cap particle count by viewport so phones don't drop frames. Each
  // particle runs 2 keyframe animations + a drop-shadow filter, which is
  // GPU-expensive at scale. Empirical sweet spot on iOS Safari: ~25 on
  // phone, ~50 on desktop. (User report 2026-05-13 "app is very slow".)
  const isMobile = typeof window !== "undefined" && window.innerWidth < 640;
  const reduced = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const target = count ?? (isMobile ? 22 : 45);
  const effectiveCount = reduced ? 0 : target;

  const particles = useMemo(() => {
    if (!effectiveCount) return [];
    const rng = (min, max) => min + Math.random() * (max - min);
    return Array.from({ length: effectiveCount }).map((_, i) => {
      const color = COLORS[Math.floor(Math.random() * COLORS.length)];
      const shapeFn = SHAPES[Math.floor(Math.random() * SHAPES.length)];
      const size = rng(4, 14);
      return {
        i,
        left: rng(-2, 102), // %
        delay: rng(0, 12), // s
        duration: rng(6, 14), // s
        drift: rng(-60, 60), // px lateral drift over the fall
        rotate: rng(0, 360), // deg start
        rotateEnd: rng(180, 720), // deg end
        size,
        color,
        bg: shapeFn(color),
        twinkleDuration: rng(1.4, 2.6),
      };
    });
  }, [effectiveCount]);

  if (!effectiveCount) return null;
  const positionClass = scope === "absolute" ? "absolute" : "fixed";

  return (
    <div
      className={`${positionClass} inset-0 pointer-events-none overflow-hidden gold-glitter-layer`}
      aria-hidden="true"
      data-testid={testId}
      style={{ zIndex: 5 }}
    >
      {particles.map((p) => (
        <span
          key={p.i}
          className="gold-glitter-particle"
          style={{
            left: `${p.left}%`,
            width: `${p.size}px`,
            height: `${p.size}px`,
            backgroundImage: `url("${p.bg}")`,
            animationDelay: `${p.delay}s, ${p.delay}s`,
            animationDuration: `${p.duration}s, ${p.twinkleDuration}s`,
            "--gg-drift": `${p.drift}px`,
            "--gg-rot-start": `${p.rotate}deg`,
            "--gg-rot-end": `${p.rotateEnd}deg`,
            // Dropped the drop-shadow filter 2026-05-13 — it was the
            // single biggest perf cost (each animated particle becomes
            // its own GPU compositing layer with a continuous blur pass).
            // Brighter base color gives the same eye-catch without the
            // per-frame shadow recompute.
          }}
        />
      ))}
    </div>
  );
}
